import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import {
  signIn,
  confirmSignInChallenge,
  requestPasswordReset,
  confirmPasswordReset,
  authErrorMessage,
} from "../lib/auth";

type Stage = "credentials" | "new_password_required" | "request_reset" | "confirm_reset";

export default function SignIn() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetInfo, setResetInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await signIn(email, password);
      handleSignInResult(result);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSignInResult = (result: Awaited<ReturnType<typeof signIn>>) => {
    if (result.isSignedIn) {
      navigate("/", { replace: true });
      return;
    }
    const step = result.nextStep?.signInStep;
    if (step === "CONFIRM_SIGN_UP") {
      navigate(`/verify?email=${encodeURIComponent(email)}`, { replace: true });
      return;
    }
    if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setStage("new_password_required");
      return;
    }
    if (step === "RESET_PASSWORD") {
      // Cognito says this user must reset their password before signing in.
      // Send them straight into the reset flow — kick off resetPassword so
      // they receive a code, then land on the confirm stage.
      void (async () => {
        try {
          await requestPasswordReset(email);
          setResetInfo(`We emailed a confirmation code to ${email}.`);
          setStage("confirm_reset");
        } catch (err) {
          setError(authErrorMessage(err));
        }
      })();
      return;
    }
    setError(`Sign-in needs an additional step (${step ?? "unknown"}). If this keeps happening, contact support.`);
  };

  const onSubmitNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setLoading(true);
    try {
      const result = await confirmSignInChallenge(newPassword);
      handleSignInResult(result);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResetInfo(null);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setResetInfo(`We emailed a confirmation code to ${email}. It can take a minute to arrive.`);
      setStage("confirm_reset");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitConfirmReset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset({ email, code: resetCode.trim(), newPassword });
      // After successful reset, drop back to credentials with the new password
      // pre-filled so the user can sign in with one click.
      setStage("credentials");
      setPassword(newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setResetCode("");
      setResetInfo("Password updated. Sign in with your new password.");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const goToRequestReset = () => {
    setError(null);
    setResetInfo(null);
    setStage("request_reset");
  };

  if (stage === "new_password_required") {
    return (
      <AuthShell
        title="Set a new password"
        subtitle="Your account was created by an administrator. Choose a new password before continuing."
      >
        <form onSubmit={onSubmitNewPassword} className="space-y-4">
          <Input
            name="email"
            type="email"
            label="Email"
            value={email}
            disabled
            readOnly
          />
          <Input
            name="newPassword"
            type="password"
            label="New password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            hint="At least 12 chars with upper, lower, number, and symbol."
          />
          <Input
            name="confirmPassword"
            type="password"
            label="Confirm password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full" size="md">
            Set password and continue
          </Button>
          <button
            type="button"
            className="block w-full text-center text-sm text-gray-500 hover:text-gray-900"
            onClick={() => {
              setStage("credentials");
              setNewPassword("");
              setConfirmPassword("");
              setError(null);
            }}
          >
            Cancel
          </button>
        </form>
      </AuthShell>
    );
  }

  if (stage === "request_reset") {
    return (
      <AuthShell
        title="Reset your password"
        subtitle="We'll email you a confirmation code to set a new password."
      >
        <form onSubmit={onSubmitRequestReset} className="space-y-4">
          <Input
            name="email"
            type="email"
            label="Email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full" size="md">
            Send code
          </Button>
          <button
            type="button"
            className="block w-full text-center text-sm text-gray-500 hover:text-gray-900"
            onClick={() => {
              setStage("credentials");
              setError(null);
            }}
          >
            Back to sign in
          </button>
        </form>
      </AuthShell>
    );
  }

  if (stage === "confirm_reset") {
    return (
      <AuthShell
        title="Set a new password"
        subtitle="Enter the code we emailed you, then choose a new password."
      >
        <form onSubmit={onSubmitConfirmReset} className="space-y-4">
          <Input
            name="email"
            type="email"
            label="Email"
            value={email}
            disabled
            readOnly
          />
          <Input
            name="code"
            type="text"
            label="Confirmation code"
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
          />
          <Input
            name="newPassword"
            type="password"
            label="New password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            hint="At least 12 chars with upper, lower, number, and symbol."
          />
          <Input
            name="confirmPassword"
            type="password"
            label="Confirm password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {resetInfo && !error && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {resetInfo}
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full" size="md">
            Set password
          </Button>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <button
              type="button"
              className="hover:text-gray-900"
              onClick={() => {
                setStage("request_reset");
                setResetCode("");
                setError(null);
              }}
            >
              Resend code
            </button>
            <button
              type="button"
              className="hover:text-gray-900"
              onClick={() => {
                setStage("credentials");
                setError(null);
              }}
            >
              Back to sign in
            </button>
          </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in to Triage"
      subtitle="Manage your AI inbox"
      footer={
        <>
          New to Triage?{" "}
          <Link to="/signup" className="font-medium text-gray-900 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
        <Input
          name="password"
          type="password"
          label="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="-mt-2 text-right">
          <button
            type="button"
            onClick={goToRequestReset}
            className="text-sm text-gray-500 hover:text-gray-900"
          >
            Forgot password?
          </button>
        </div>
        {resetInfo && !error && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {resetInfo}
          </div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <Button type="submit" loading={loading} className="w-full" size="md">
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
